import React from 'react';
import ReactDOM from 'react-dom';
import './vendor.module';
import 'styles/main.scss';

// Sentry
import './error-reporting';
import {
    initRootStore,
    createEnvironmentDetect,
    getRootStore,
    initGlobalExceptionHandler,
    configureDashboardTracing,
    configureUiInfra,
    addErrorEventLog
} from './_react_/app.bootstrap';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {TechseeLiveApp} from './app.react';
import {getAppTracer} from './app.tracer';
import {checkAndRedirectHashUrl} from './_react_/router/hash-url-redirect';
import TsUtils from '@techsee/techsee-common/lib/utils';
// @ts-ignore
import {getBackendUrl} from '@techsee/techsee-common/lib/utils';

declare const SOCKET_URL: string;
declare const API_URL: string;
declare const STATS_API_URL: string;
declare const MOBILE_CLIENT_URL: string;
declare const CLIENT_START_URL: string;

const trace = getAppTracer('app-react.module');

// Initialize React application
async function initReactApp() {
    try {
        // This must happen before React Router initializes
        const wasRedirected = checkAndRedirectHashUrl();
        if (wasRedirected) {
            // Redirect initiated, app will reload with new URL
            // Don't continue with React initialization
            return;
        }

        // Continue with normal React app initialization
        const environmentDetect = await createEnvironmentDetect();

        await initRootStore({
            globals: {window},
            config: {
                socketUrl: getBackendUrl(SOCKET_URL, {
                    hostname: window.location.hostname,
                    isStat: false,
                    ENV: {
                        API_URL,
                        SOCKET_URL,
                        STATS_API_URL,
                        MOBILE_CLIENT_URL
                    }
                }),
                mobileClientURL: (TsUtils as any).replaceUrlDomain(CLIENT_START_URL, window.location.hostname)
            },
            dependencies: {
                environmentDetect
            }
        });

        await getRootStore().tsLocaleHelper.loadLocale();

        initGlobalExceptionHandler();

        configureDashboardTracing();
        configureUiInfra();

        const rootElement = document.getElementById('app-root-element');

        if (!rootElement) {
            throw new Error('Root element not found. Expected #app-root-element');
        }

        rootElement.classList.add('full-height');

        const isMobile = getRootStore().environmentService.isMobile(getRootStore().displayTabletAsDesktop);

        if (isMobile) {
            rootElement.classList.add('mobile-top-view');
        }

        ReactDOM.render(<TechseeLiveApp />, rootElement);
        trace.info('React app rendered successfully');
    } catch (error) {
        trace.error('Failed to initialize React app:', error);

        addErrorEventLog(LOG_EVENTS.appInitError.type, {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
        });

        // Show error message
        const rootElement = document.getElementById('app-root-element') || document.getElementById('root');
        if (rootElement) {
            rootElement.innerHTML = `<div style="padding: 20px; color: red;">
                <h2>Failed to initialize application</h2>
                <p>Error: ${error instanceof Error ? error.message : String(error)}</p>
                <p>Please check the browser console for more details.</p>
            </div>`;
        }
    }
}

// Start initialization
initReactApp();
